/**
 * QUIPWA — StateDBService
 *
 * Every read and write of `_state.db`: deck instances, FSRS review state and
 * log, sessions, tags.
 *
 * Replication contract (docs/modulos/replicacion.md):
 *   - every write to a replicated row stamps `updated_at` with this device's
 *     hybrid logical clock;
 *   - every delete leaves a tombstone, so the row cannot be resurrected by a
 *     merge with a replica that still has it;
 *   - review_log is append-only (a grow-only set; never UPDATE or DELETE it
 *     outside a deck deletion).
 *
 * Schema migrations run in the DB factory before this class is constructed.
 */

import { v4 as uuidv4 } from 'uuid'
import type { IDBAdapter } from './db-adapter.ts'
import type {
  CardState,
  DeckInstance,
  DeckStats,
  ReviewLog,
  ReviewState,
  Session,
  SessionDeck,
  SessionMode,
  Tag,
  TagWithStats,
} from '../../types/domain.ts'
import { averageRetention, averageRetentionByDeck } from '../fsrs/deck-retention.ts'
import type { HybridClock } from '../replication/hlc.ts'
import { getDeviceClock } from '../replication/device-id.ts'
import { tombstoneKey, type TombstoneEntity } from '../replication/state-tables.ts'

export interface SessionWithMeta {
  id: string
  started_at: number
  ended_at: number | null
  mode: number
  cards_total: number
  cards_reviewed: number
  avg_time_ms: number | null
  cal_event_id: string | null
  /** Name of the session's first deck. */
  deck_name: string | null
  /** Id of the session's first deck (for navigation). */
  deck_instance_id: string | null
  /** Fraction of ratings ≥ 3, or null without reviews. */
  accuracy: number | null
}

export interface ReviewStateUpdate {
  due: number
  stability: number
  difficulty: number
  elapsed_days: number
  scheduled_days: number
  reps: number
  lapses: number
  state: CardState
  last_review: number
  avg_time_ms: number
}

const nowSeconds = () => Math.floor(Date.now() / 1000)

export class StateDBService {
  private readonly db: IDBAdapter
  private readonly clock: HybridClock

  constructor(db: IDBAdapter, clock: HybridClock = getDeviceClock()) {
    this.db = db
    this.clock = clock
  }

  /** The adapter, for the sync engine (export/apply). */
  get adapter(): IDBAdapter {
    return this.db
  }

  /** Persist pending writes now. */
  async flushNow(): Promise<void> {
    await this.db.flushNow?.()
  }

  // ─── internals ────────────────────────────────────────────────────────────

  private stamp(): string {
    return this.clock.now()
  }

  private async tombstone(db: IDBAdapter, entity: TombstoneEntity, key: string, deletedAt = this.stamp()): Promise<void> {
    await db.run(
      `INSERT INTO tombstones (entity, key, deleted_at) VALUES (?, ?, ?)
       ON CONFLICT(entity, key) DO UPDATE SET deleted_at = excluded.deleted_at
       WHERE excluded.deleted_at > tombstones.deleted_at`,
      [entity, key, deletedAt]
    )
  }


  // ─── SETTINGS (replicated, last-writer-wins per key) ──────────────────────

  async getSetting<T>(key: string): Promise<T | null> {
    const row = await this.db.queryOne<{ value: string }>('SELECT value FROM settings WHERE key = ?', [key])
    if (!row) return null
    try {
      return JSON.parse(row.value) as T
    } catch {
      return null
    }
  }

  /** Every setting whose key starts with `prefix` (e.g. `theme.profile.`). Deleted ones come back as null. */
  async listSettings<T>(prefix: string): Promise<Array<{ key: string; value: T | null }>> {
    const rows = await this.db.query<{ key: string; value: string }>(
      "SELECT key, value FROM settings WHERE key >= ? AND key < ? ORDER BY key",
      [prefix, `${prefix}￿`]
    )
    return rows.map((r) => {
      try {
        return { key: r.key, value: JSON.parse(r.value) as T | null }
      } catch {
        return { key: r.key, value: null }
      }
    })
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    await this.db.run(
      `INSERT INTO settings (key, value, updated_at) VALUES (?, ?, ?)
       ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
      [key, JSON.stringify(value), this.stamp()]
    )
  }

  // ─── DECK INSTANCES ───────────────────────────────────────────────────────

  async getDeckInstances(): Promise<DeckInstance[]> {
    return this.db.query<DeckInstance>('SELECT * FROM deck_instances ORDER BY imported_at DESC')
  }

  async getDeckInstance(id: string): Promise<DeckInstance | null> {
    return this.db.queryOne<DeckInstance>('SELECT * FROM deck_instances WHERE id = ?', [id])
  }

  async getDeckInstanceByFileId(driveFileId: string): Promise<DeckInstance | null> {
    return this.db.queryOne<DeckInstance>('SELECT * FROM deck_instances WHERE drive_file_id = ?', [driveFileId])
  }

  async insertDeckInstance(inst: Omit<DeckInstance, 'id'>): Promise<DeckInstance> {
    const id = uuidv4()
    await this.db.run(
      `INSERT INTO deck_instances
         (id, deck_id, deck_name, wflash_drive_path, drive_file_id,
          drive_modified_at, imported_at, card_count, card_style, deck_color, updated_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?)`,
      [
        id, inst.deck_id, inst.deck_name, inst.wflash_drive_path, inst.drive_file_id,
        inst.drive_modified_at, inst.imported_at, inst.card_count,
        inst.card_style ?? null, inst.deck_color ?? null, this.stamp(),
      ]
    )
    return { id, ...inst }
  }

  /**
   * Bind a deck to its Drive file after its first successful upload. Decks
   * created locally (e.g. from a note) start with an empty drive_file_id.
   */
  async markDeckUploaded(id: string, driveFileId: string, driveModifiedAt: number): Promise<void> {
    await this.db.run(
      'UPDATE deck_instances SET drive_file_id = ?, drive_modified_at = ?, updated_at = ? WHERE id = ?',
      [driveFileId, driveModifiedAt, this.stamp(), id]
    )
  }

  async updateDeckModifiedAt(id: string, driveModifiedAt: number): Promise<void> {
    await this.db.run('UPDATE deck_instances SET drive_modified_at = ?, updated_at = ? WHERE id = ?', [
      driveModifiedAt, this.stamp(), id,
    ])
  }

  async updateDeckVisuals(id: string, deckColor: string | null, cardStyle: string | null): Promise<void> {
    await this.db.run('UPDATE deck_instances SET deck_color = ?, card_style = ?, updated_at = ? WHERE id = ?', [
      deckColor, cardStyle, this.stamp(), id,
    ])
  }

  async updateCardCount(id: string, count: number): Promise<void> {
    await this.db.run('UPDATE deck_instances SET card_count = ?, updated_at = ? WHERE id = ?', [count, this.stamp(), id])
  }

  /**
   * Delete a deck with its review state and tag links, leaving tombstones for
   * the deck and for its Drive file (so discovery never re-imports it).
   */
  async deleteDeckInstance(id: string): Promise<void> {
    const deck = await this.getDeckInstance(id)
    await this.db.transaction(async (db) => {
      const stamp = this.stamp()
      await this.tombstone(db, 'deck_instance', tombstoneKey.deckInstance(id), stamp)
      if (deck?.drive_file_id) await this.tombstone(db, 'deck_file', tombstoneKey.deckFile(deck.drive_file_id), stamp)
      await db.run('DELETE FROM review_state WHERE deck_instance_id = ?', [id])
      await db.run('DELETE FROM review_log WHERE deck_instance_id = ?', [id])
      await db.run('DELETE FROM deck_tags WHERE deck_instance_id = ?', [id])
      await db.run('DELETE FROM deck_instances WHERE id = ?', [id])
    })
  }

  /** Drive file ids of decks the user deleted (never re-import these). */
  async getDeletedDeckFileIds(): Promise<Set<string>> {
    const rows = await this.db.query<{ key: string }>("SELECT key FROM tombstones WHERE entity = 'deck_file'")
    return new Set(rows.map((r) => r.key))
  }

  // ─── REVIEW STATE ─────────────────────────────────────────────────────────

  async getReviewState(cardId: string, deckInstanceId: string): Promise<ReviewState | null> {
    return this.db.queryOne<ReviewState>('SELECT * FROM review_state WHERE card_id = ? AND deck_instance_id = ?', [
      cardId, deckInstanceId,
    ])
  }

  /** Cards due or overdue for a deck instance, most overdue first. */
  async getDueCards(deckInstanceId: string, nowTs: number, limit?: number): Promise<ReviewState[]> {
    const params: (string | number)[] = [deckInstanceId, nowTs]
    if (limit) params.push(limit)
    return this.db.query<ReviewState>(
      `SELECT * FROM review_state
       WHERE deck_instance_id = ? AND state IN (1,2,3) AND due <= ?
       ORDER BY due ASC${limit ? ' LIMIT ?' : ''}`,
      params
    )
  }

  /** New cards (state = 0), in insertion order. */
  async getNewCards(deckInstanceId: string, limit: number): Promise<ReviewState[]> {
    return this.db.query<ReviewState>(
      'SELECT * FROM review_state WHERE deck_instance_id = ? AND state = 0 ORDER BY rowid ASC LIMIT ?',
      [deckInstanceId, limit]
    )
  }

  async getAllReviewStates(deckInstanceId: string): Promise<ReviewState[]> {
    return this.db.query<ReviewState>('SELECT * FROM review_state WHERE deck_instance_id = ?', [deckInstanceId])
  }

  /** Worst cards for free mode (most lapses, least stability). */
  async getWorstCards(deckInstanceId: string, limit: number): Promise<ReviewState[]> {
    return this.db.query<ReviewState>(
      'SELECT * FROM review_state WHERE deck_instance_id = ? ORDER BY lapses DESC, stability ASC LIMIT ?',
      [deckInstanceId, limit]
    )
  }

  /** Random cards for free mode. */
  async getRandomCards(deckInstanceId: string, limit: number): Promise<ReviewState[]> {
    return this.db.query<ReviewState>('SELECT * FROM review_state WHERE deck_instance_id = ? ORDER BY RANDOM() LIMIT ?', [
      deckInstanceId, limit,
    ])
  }

  async insertInitialReviewState(cardId: string, deckInstanceId: string, nowTs: number): Promise<void> {
    await this.insertInitialReviewStates([cardId], deckInstanceId, nowTs)
  }

  /** New cards start at state 0, due now. Existing rows are left untouched. */
  async insertInitialReviewStates(cardIds: string[], deckInstanceId: string, nowTs: number): Promise<void> {
    await this.db.transaction(async (db) => {
      for (const cardId of cardIds) {
        await db.run(
          `INSERT OR IGNORE INTO review_state
             (card_id, deck_instance_id, due, stability, difficulty, elapsed_days, scheduled_days,
              reps, lapses, state, last_review, avg_time_ms, updated_at)
           VALUES (?,?,?,0.0,5.0,0,0,0,0,0,NULL,NULL,?)`,
          [cardId, deckInstanceId, nowTs, this.stamp()]
        )
      }
    })
  }

  async updateReviewState(cardId: string, deckInstanceId: string, update: ReviewStateUpdate): Promise<void> {
    await this.db.run(
      `UPDATE review_state SET
         due = ?, stability = ?, difficulty = ?, elapsed_days = ?, scheduled_days = ?,
         reps = ?, lapses = ?, state = ?, last_review = ?, avg_time_ms = ?, updated_at = ?
       WHERE card_id = ? AND deck_instance_id = ?`,
      [
        update.due, update.stability, update.difficulty, update.elapsed_days, update.scheduled_days,
        update.reps, update.lapses, update.state, update.last_review, update.avg_time_ms, this.stamp(),
        cardId, deckInstanceId,
      ]
    )
  }

  /**
   * Record one review atomically: the new FSRS state and its log entry go
   * together or not at all.
   */
  async recordReview(update: ReviewStateUpdate & { card_id: string; deck_instance_id: string }, log: Omit<ReviewLog, 'id'>): Promise<void> {
    await this.db.transaction(async (db) => {
      const stamp = this.stamp()
      await db.run(
        `UPDATE review_state SET
           due = ?, stability = ?, difficulty = ?, elapsed_days = ?, scheduled_days = ?,
           reps = ?, lapses = ?, state = ?, last_review = ?, avg_time_ms = ?, updated_at = ?
         WHERE card_id = ? AND deck_instance_id = ?`,
        [
          update.due, update.stability, update.difficulty, update.elapsed_days, update.scheduled_days,
          update.reps, update.lapses, update.state, update.last_review, update.avg_time_ms, stamp,
          update.card_id, update.deck_instance_id,
        ]
      )
      await this.insertReviewLog(db, log)
    })
  }

  async deleteCardStates(deckInstanceId: string, cardIds: string[]): Promise<void> {
    if (cardIds.length === 0) return
    await this.db.transaction(async (db) => {
      for (const cardId of cardIds) {
        await this.tombstone(db, 'review_state', tombstoneKey.reviewState(cardId, deckInstanceId))
        await db.run('DELETE FROM review_state WHERE deck_instance_id = ? AND card_id = ?', [deckInstanceId, cardId])
      }
    })
  }

  // ─── REVIEW LOG (APPEND-ONLY) ─────────────────────────────────────────────

  /** Append a review. Never UPDATE or DELETE review_log rows. */
  async appendReviewLog(entry: Omit<ReviewLog, 'id'>): Promise<void> {
    await this.insertReviewLog(this.db, entry)
  }

  private async insertReviewLog(db: IDBAdapter, entry: Omit<ReviewLog, 'id'>): Promise<void> {
    await db.run(
      `INSERT OR IGNORE INTO review_log
         (card_id, deck_instance_id, session_id, reviewed_at, rating, time_ms,
          stability_before, stability_after, difficulty_before, difficulty_after,
          scheduled_days_before, scheduled_days_after)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?)`,
      [
        entry.card_id, entry.deck_instance_id, entry.session_id, entry.reviewed_at, entry.rating, entry.time_ms,
        entry.stability_before, entry.stability_after, entry.difficulty_before, entry.difficulty_after,
        entry.scheduled_days_before, entry.scheduled_days_after,
      ]
    )
  }

  async getReviewLogForSession(sessionId: string): Promise<ReviewLog[]> {
    return this.db.query<ReviewLog>('SELECT * FROM review_log WHERE session_id = ? ORDER BY reviewed_at ASC', [sessionId])
  }

  async getReviewLogForCard(cardId: string, deckInstanceId: string): Promise<ReviewLog[]> {
    return this.db.query<ReviewLog>(
      'SELECT * FROM review_log WHERE card_id = ? AND deck_instance_id = ? ORDER BY reviewed_at ASC',
      [cardId, deckInstanceId]
    )
  }

  // ─── DECK STATS (VIEW) ────────────────────────────────────────────────────

  /** Stats from the deck_stats view. `avg_retention` is 0 there; use the *WithRetention variant. */
  async getDeckStats(deckInstanceId: string): Promise<DeckStats | null> {
    return this.db.queryOne<DeckStats>('SELECT * FROM deck_stats WHERE deck_instance_id = ?', [deckInstanceId])
  }

  /**
   * Stats with a real average retention, computed with the scheduler's own
   * retrievability formula so deck health never drifts from scheduling.
   */
  async getDeckStatsWithRetention(deckInstanceId: string): Promise<DeckStats | null> {
    const stats = await this.getDeckStats(deckInstanceId)
    if (!stats) return null
    const rows = await this.db.query<{ stability: number; last_review: number | null }>(
      'SELECT stability, last_review FROM review_state WHERE deck_instance_id = ? AND state > 0 AND stability > 0',
      [deckInstanceId]
    )
    return { ...stats, avg_retention: averageRetention(rows, nowSeconds()) }
  }

  /** Average retention for every deck in one query (the view hardcodes 0). */
  private async getRetentionByDeck(): Promise<Map<string, number>> {
    const rows = await this.db.query<{ deck_instance_id: string; stability: number; last_review: number | null }>(
      'SELECT deck_instance_id, stability, last_review FROM review_state WHERE state > 0 AND stability > 0'
    )
    return averageRetentionByDeck(rows, nowSeconds())
  }

  private mergeRetention(statsRows: DeckStats[], retention: Map<string, number>): Map<string, DeckStats> {
    return new Map(statsRows.map((s) => [s.deck_instance_id, { ...s, avg_retention: retention.get(s.deck_instance_id) ?? null }]))
  }

  /** @deprecated deck_stats is a view; kept so older callers compile. */
  async recomputeDeckStats(_deckInstanceId: string): Promise<void> {}

  // ─── SESSIONS ─────────────────────────────────────────────────────────────

  async createSession(mode: SessionMode): Promise<Session> {
    const id = uuidv4()
    const startedAt = nowSeconds()
    await this.db.run(
      `INSERT INTO sessions (id, started_at, ended_at, mode, cards_total, cards_reviewed, updated_at)
       VALUES (?,?,NULL,?,0,0,?)`,
      [id, startedAt, mode, this.stamp()]
    )
    return { id, started_at: startedAt, ended_at: null, mode, cards_total: 0, cards_reviewed: 0, avg_time_ms: null, cal_event_id: null }
  }

  async closeSession(id: string, cardsTotal: number, cardsReviewed: number, avgTimeMs: number | null): Promise<void> {
    await this.db.run(
      `UPDATE sessions SET ended_at = ?, cards_total = ?, cards_reviewed = ?, avg_time_ms = ?, updated_at = ?
       WHERE id = ?`,
      [nowSeconds(), cardsTotal, cardsReviewed, avgTimeMs, this.stamp(), id]
    )
  }

  /** Link a session to the reminder task created for it (stored in cal_event_id). */
  async setSessionCalendarEvent(sessionId: string, calEventId: string): Promise<void> {
    await this.db.run('UPDATE sessions SET cal_event_id = ?, updated_at = ? WHERE id = ?', [calEventId, this.stamp(), sessionId])
  }

  async getSession(id: string): Promise<Session | null> {
    return this.db.queryOne<Session>('SELECT * FROM sessions WHERE id = ?', [id])
  }

  /**
   * Finished sessions with their first deck and accuracy, newest first.
   * @param deckInstanceId restrict to sessions that included this deck
   */
  async getRecentSessionsWithMeta(limit: number, deckInstanceId?: string): Promise<SessionWithMeta[]> {
    const params: (string | number)[] = []
    if (deckInstanceId) params.push(deckInstanceId)
    params.push(limit)
    return this.db.query<SessionWithMeta>(
      `SELECT
         s.id, s.started_at, s.ended_at, s.mode, s.cards_total, s.cards_reviewed, s.avg_time_ms, s.cal_event_id,
         MIN(di.deck_name)        AS deck_name,
         MIN(sd.deck_instance_id) AS deck_instance_id,
         AVG(CASE WHEN rl.rating >= 3 THEN 1.0 ELSE 0.0 END) AS accuracy
       FROM sessions s
       LEFT JOIN session_decks sd ON sd.session_id = s.id
       LEFT JOIN deck_instances di ON di.id = sd.deck_instance_id
       LEFT JOIN review_log rl ON rl.session_id = s.id
       WHERE s.ended_at IS NOT NULL ${deckInstanceId ? 'AND sd.deck_instance_id = ?' : ''}
       GROUP BY s.id
       ORDER BY s.started_at DESC
       LIMIT ?`,
      params
    )
  }

  /** Fraction of ratings ≥ 3 in a session, or null without reviews. */
  async getSessionAccuracy(sessionId: string): Promise<number | null> {
    const row = await this.db.queryOne<{ accuracy: number | null }>(
      'SELECT AVG(CASE WHEN rating >= 3 THEN 1.0 ELSE 0.0 END) AS accuracy FROM review_log WHERE session_id = ?',
      [sessionId]
    )
    return row?.accuracy ?? null
  }

  async addSessionDeck(sessionId: string, deckInstanceId: string, cardsFromDeck: number): Promise<void> {
    await this.db.run(
      `INSERT OR REPLACE INTO session_decks (session_id, deck_instance_id, cards_from_deck, updated_at)
       VALUES (?,?,?,?)`,
      [sessionId, deckInstanceId, cardsFromDeck, this.stamp()]
    )
  }

  async getSessionDecks(sessionId: string): Promise<SessionDeck[]> {
    return this.db.query<SessionDeck>('SELECT * FROM session_decks WHERE session_id = ?', [sessionId])
  }

  // ─── CARD RECONCILIATION (deck content updated in Drive) ─────────────────

  /**
   * After a .wflash update: new card ids start at state 0, existing ones keep
   * their FSRS state, removed ones are deleted (with tombstones).
   */
  async reconcileCards(deckInstanceId: string, allCardIds: string[]): Promise<void> {
    const existing = new Set(
      (await this.db.query<{ card_id: string }>('SELECT card_id FROM review_state WHERE deck_instance_id = ?', [deckInstanceId]))
        .map((r) => r.card_id)
    )
    const incoming = new Set(allCardIds)
    const added = allCardIds.filter((id) => !existing.has(id))
    const removed = [...existing].filter((id) => !incoming.has(id))

    if (added.length) await this.insertInitialReviewStates(added, deckInstanceId, nowSeconds())
    if (removed.length) await this.deleteCardStates(deckInstanceId, removed)
  }

  // ─── GLOBAL STATS ─────────────────────────────────────────────────────────

  async getTotalDueAcrossDecks(nowTs: number): Promise<number> {
    const row = await this.db.queryOne<{ count: number }>(
      'SELECT COUNT(*) AS count FROM review_state WHERE due <= ? AND state IN (1,2,3)',
      [nowTs]
    )
    return row?.count ?? 0
  }

  async getDeckInstancesWithStats(): Promise<Array<DeckInstance & { stats: DeckStats | null }>> {
    const [instances, statsRows, retention] = await Promise.all([
      this.getDeckInstances(),
      this.db.query<DeckStats>('SELECT * FROM deck_stats'),
      this.getRetentionByDeck(),
    ])
    const byDeck = this.mergeRetention(statsRows, retention)
    return instances.map((inst) => ({ ...inst, stats: byDeck.get(inst.id) ?? null }))
  }

  /** review_state rows for a deck (0 means "not hydrated on this device"). */
  async getCardCountForDeck(deckInstanceId: string): Promise<number> {
    const row = await this.db.queryOne<{ count: number }>(
      'SELECT COUNT(*) AS count FROM review_state WHERE deck_instance_id = ?',
      [deckInstanceId]
    )
    return row?.count ?? 0
  }

  // ─── TAGS ─────────────────────────────────────────────────────────────────
  //
  // Two devices can create the same tag name offline; both rows replicate
  // (the merge must not depend on which device synced first). Reads go through
  // the `tag_canon` view, which maps every tag id to the canonical id for its
  // name (oldest, then lowest id), so the UI always sees one tag per name.
  // Writes addressed to a canonical id apply to every row sharing its name.

  /** Ids of every tag row sharing a name with `tagId` (its aliases, itself included). */
  private async aliasIds(db: IDBAdapter, tagId: string): Promise<string[]> {
    const rows = await db.query<{ id: string }>(
      'SELECT t.id FROM tags t WHERE t.name = (SELECT name FROM tags WHERE id = ?)',
      [tagId]
    )
    return rows.length ? rows.map((r) => r.id) : [tagId]
  }

  async getAllTags(): Promise<TagWithStats[]> {
    return this.db.query<TagWithStats>(`
      SELECT t.id, t.name, t.parent_id, t.color, t.created_at,
             (SELECT COUNT(DISTINCT dt.deck_instance_id)
                FROM deck_tags dt JOIN tag_canon c ON c.tag_id = dt.tag_id
               WHERE c.canon_id = t.id) AS decks_count,
             (SELECT COALESCE(SUM(ds.total_cards), 0) FROM deck_stats ds
               WHERE ds.deck_instance_id IN (
                 SELECT dt.deck_instance_id FROM deck_tags dt JOIN tag_canon c ON c.tag_id = dt.tag_id
                  WHERE c.canon_id = t.id)) AS cards_count
      FROM tags t JOIN tag_canon c ON c.tag_id = t.id AND c.canon_id = t.id
      ORDER BY t.created_at DESC
    `)
  }

  async getTagsForDeck(deckInstanceId: string): Promise<Tag[]> {
    return this.db.query<Tag>(
      `SELECT DISTINCT t.id, t.name, t.parent_id, t.color, t.created_at
       FROM deck_tags dt
       JOIN tag_canon c ON c.tag_id = dt.tag_id
       JOIN tags t ON t.id = c.canon_id
       WHERE dt.deck_instance_id = ?`,
      [deckInstanceId]
    )
  }

  async getDecksByTag(tagId: string): Promise<Array<DeckInstance & { stats: DeckStats | null }>> {
    const aliases = await this.aliasIds(this.db, tagId)
    const placeholders = aliases.map(() => '?').join(',')
    const rows = await this.db.query<DeckInstance>(
      `SELECT DISTINCT d.* FROM deck_instances d JOIN deck_tags dt ON d.id = dt.deck_instance_id
       WHERE dt.tag_id IN (${placeholders})`,
      aliases
    )
    const [statsRows, retention] = await Promise.all([this.db.query<DeckStats>('SELECT * FROM deck_stats'), this.getRetentionByDeck()])
    const byDeck = this.mergeRetention(statsRows, retention)
    return rows.map((row) => ({ ...row, stats: byDeck.get(row.id) ?? null }))
  }

  /**
   * Names are stored trimmed and lowercase, so "Ciencia" and "ciencia" are the
   * same tag. Creating a name that already exists returns the existing tag.
   */
  async createTag(name: string, color: string, parentId: string | null = null): Promise<Tag> {
    const parsedName = name.trim().toLowerCase()
    const existing = await this.db.queryOne<Tag>(
      `SELECT t.id, t.name, t.parent_id, t.color, t.created_at
       FROM tags t JOIN tag_canon c ON c.tag_id = t.id AND c.canon_id = t.id
       WHERE t.name = ?`,
      [parsedName]
    )
    if (existing) return existing

    const id = uuidv4()
    const createdAt = nowSeconds()
    await this.db.run(
      'INSERT INTO tags (id, name, parent_id, color, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
      [id, parsedName, parentId, color, createdAt, this.stamp()]
    )
    return { id, name: parsedName, parent_id: parentId, color, created_at: createdAt }
  }

  /** Rename/recolor a tag — every row sharing its name, so no alias is left behind. */
  async updateTag(id: string, name: string, color: string, parentId: string | null = null): Promise<void> {
    await this.db.transaction(async (db) => {
      for (const aliasId of await this.aliasIds(db, id)) {
        await db.run('UPDATE tags SET name = ?, color = ?, parent_id = ?, updated_at = ? WHERE id = ?', [
          name.trim().toLowerCase(), color, parentId, this.stamp(), aliasId,
        ])
      }
    })
  }

  /** Delete a tag (and its same-name aliases) with tombstones; its links go too. */
  async deleteTag(id: string): Promise<void> {
    await this.db.transaction(async (db) => {
      for (const aliasId of await this.aliasIds(db, id)) {
        await this.tombstone(db, 'tag', tombstoneKey.tag(aliasId))
        await db.run('DELETE FROM deck_tags WHERE tag_id = ?', [aliasId])
        await db.run('DELETE FROM tags WHERE id = ?', [aliasId])
      }
    })
  }

  async assignTagToDeck(deckInstanceId: string, tagId: string): Promise<void> {
    await this.db.run(
      `INSERT INTO deck_tags (deck_instance_id, tag_id, updated_at) VALUES (?, ?, ?)
       ON CONFLICT(deck_instance_id, tag_id) DO NOTHING`,
      [deckInstanceId, tagId, this.stamp()]
    )
  }

  async removeTagFromDeck(deckInstanceId: string, tagId: string): Promise<void> {
    await this.setDeckTags(
      deckInstanceId,
      (await this.getTagsForDeck(deckInstanceId)).map((t) => t.id).filter((id) => id !== tagId)
    )
  }

  /**
   * Replace a deck's tags (canonical ids, as the UI shows them). Removed tags
   * lose every alias link, each with a tombstone; kept links stay untouched.
   */
  async setDeckTags(deckInstanceId: string, tagIds: string[]): Promise<void> {
    const wanted = new Set(tagIds)
    await this.db.transaction(async (db) => {
      const links = await db.query<{ tag_id: string; canon_id: string }>(
        `SELECT dt.tag_id, c.canon_id FROM deck_tags dt JOIN tag_canon c ON c.tag_id = dt.tag_id
         WHERE dt.deck_instance_id = ?`,
        [deckInstanceId]
      )
      const present = new Set(links.map((l) => l.canon_id))
      for (const link of links) {
        if (wanted.has(link.canon_id)) continue
        await this.tombstone(db, 'deck_tag', tombstoneKey.deckTag(deckInstanceId, link.tag_id))
        await db.run('DELETE FROM deck_tags WHERE deck_instance_id = ? AND tag_id = ?', [deckInstanceId, link.tag_id])
      }
      for (const tagId of wanted) {
        if (present.has(tagId)) continue
        await db.run(
          `INSERT INTO deck_tags (deck_instance_id, tag_id, updated_at) VALUES (?, ?, ?)
           ON CONFLICT(deck_instance_id, tag_id) DO UPDATE SET updated_at = excluded.updated_at`,
          [deckInstanceId, tagId, this.stamp()]
        )
      }
    })
  }
}